import { DestroyRef, Injectable, NgZone } from '@angular/core';
import posthog, { type PostHog } from 'posthog-js';

import moment from 'moment';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

import {
	PostHogOrderEvent,
	OrderItem,
	PostHogEvent,
	BookInfoEnteredEvent,
	SiteListViewedEvent,
	ProductListViewedEvent,
	ProductSelectedEvent,
	DateSelectedEvent,
	BookUrlSubmittedEvent,
	ProductAddedToCartEvent,
	CheckoutStartedEvent,
	OrderCompletedEvent,
	UserLoggedInEvent,
	UserLoginFailedEvent,
	PasswordResetRequestedEvent,
	PasswordResetCompletedEvent,
	AccountCreatedEvent,
	AccountConfirmedEvent,
	ProductRemovedFromCartEvent,
	PromoCodeAppliedEvent,
	LeadMagnetInfoEnteredEvent,
	LeadMagnetFundingSelectedEvent,
} from '../../types/datatypes';

@Injectable({
	providedIn: 'root',
})
export class PosthogService {
	// EVENTS

	// FEATURES STEP 1 EVENTS
	public SITE_LIST_VIEWED: SiteListViewedEvent['event'] = 'site_list_viewed';

	// FEATURES STEP 2, PROMOSTACKS STEP 1, MEMBERSHIPS - EVENTS
	public PRODUCT_LIST_VIEWED: ProductListViewedEvent['event'] = 'product_list_viewed';

	public PRODUCT_SELECTED: ProductSelectedEvent['event'] = 'product_selected';

	// FEATURES STEP 3 EVENTS
	public DATE_SELECTED: DateSelectedEvent['event'] = 'date_selected';

	// FEATURES STEP 4 EVENTS
	public BOOK_URL_SUBMITTED: BookUrlSubmittedEvent['event'] = 'book_url_submitted';

	public BOOK_INFO_ENTERED: BookInfoEnteredEvent['event'] = 'book_info_entered'; // Features Step 4 submission

	// FEATURES STEP 5 EVENTS
	public PRODUCT_ADDED_TO_CART: ProductAddedToCartEvent['event'] = 'product_added_to_cart';

	public PRODUCT_REMOVED_FROM_CART: ProductRemovedFromCartEvent['event'] = 'product_removed_from_cart';

	public PROMO_CODE_APPLIED: PromoCodeAppliedEvent['event'] = 'promo_code_applied';

	// CHECKOUT EVENTS
	public CHECKOUT_STARTED: CheckoutStartedEvent['event'] = 'checkout_started';

	// Only subscriptions trigger this event in the frontend
	public ORDER_COMPLETED: OrderCompletedEvent['event'] = 'order_completed';

	// AUTH EVENTS
	public USER_LOGGED_IN: UserLoggedInEvent['event'] = 'user_logged_in';

	public USER_LOGIN_FAILED: UserLoginFailedEvent['event'] = 'user_login_failed';

	public PASSWORD_RESET_REQUESTED: PasswordResetRequestedEvent['event'] = 'password_reset_requested';

	public PASSWORD_RESET_COMPLETED: PasswordResetCompletedEvent['event'] = 'password_reset_completed';

	public ACCOUNT_CREATED: AccountCreatedEvent['event'] = 'account_created';

	public ACCOUNT_CONFIRMED: AccountConfirmedEvent['event'] = 'account_confirmed';

	// LEAD MAGNET FORM EVENTS
	public LEAD_MAGNET_INFO_ENTERED: LeadMagnetInfoEnteredEvent['event'] = 'lead_magnet_info_entered';

	public LEAD_MAGNET_FUNDING_SELECTED: LeadMagnetFundingSelectedEvent['event'] = 'lead_magnet_funding_selected';

	// I wanted to use userService, but it makes more sense to import posthog into
	// user service so we can pipe off login/logout for identify()
	// I dont love making this a side effect of identify()/reset(), but it will do for now.
	// We could layer this service instead I suppose.
	private loggedIn = false;

	private identifiedUser;

	private orderEvent: PostHogOrderEvent;

	private url: string;

	private internalDomains = ['@writtenwordmedia.com', '@zerocopylabs.com'];

	private posthog!: PostHog;

	constructor(
		private ngZone: NgZone,
		private router: Router,
		private destroyRef: DestroyRef,
	) {
		this.initPostHog();
	}

	// Most events are captured as a side effect of the service's API call.
	// This makes the handling centralized and prevents new method calls from missing
	// the event invocation.
	capture<K extends PostHogEvent['event']>(
		event: K,
		properties?: Extract<PostHogEvent, { event: K }>['properties'],
	): void {
		this.posthog.capture(event, {
			is_logged_in: this.loggedIn,
			...properties,
		});
	}

	identify(
		userId: string,
		properties?: { email: string | null | undefined; membership_level: string | null | undefined; account_created_at: string },
	): void {
		// Bail early if we have already identified as the current user.
		if (userId === this.identifiedUser) {
			return;
		}
		const { email, ...props } = properties;
		const isInternal = this.internalDomains.some((domain) => (email ?? '').endsWith(domain));

		this.posthog.identify(userId, {
			...props,
			is_internal_user: isInternal,
		});

		this.identifiedUser = userId;
		this.loggedIn = true;
	}

	reset(): void {
		this.posthog.reset();
		this.loggedIn = false;
	}

	page(path: string): void {
		this.posthog.capture('$pageview', { $current_url: path });
	}

	cacheOrder(orderEvent: PostHogOrderEvent) {
		this.orderEvent = orderEvent;
	}

	isOrderCached(orderEvent: PostHogOrderEvent) {
		return this.orderEvent
			&& this.orderEvent.order_id === orderEvent.order_id
			&& this.orderEvent.cart_value === orderEvent.cart_value
			&& this.orderEvent.cart_item_count === orderEvent.cart_item_count;
	}

	getPosthogProduct(orderItem: OrderItem) {
		const { itemType: type, item } = orderItem;
		if (type === 'featuredBook') {
			return {
				product_type: type,
				product_id: item._siteAdvert._id,
				product_name: item._siteAdvert.name,
				feature_id: item._id,
				book_id: item._book._id,
				price: item.paymentAmount,
				promo_date: moment(item.pubDate, 'YYYYMMDD').toISOString(),
				site: item._site.abbreviatedName,
			};
		}

		if (type === 'promoStack') {
			return {
				product_type: type,
				product_id: item._promoStack._id,
				product_name: item._promoStack.name,
				book_id: item._book._id,
				price: item.paymentAmount,
			};
		}

		if (type === 'subscription') {
			return {
				product_type: type,
				product_id: item.plan._id,
				product_name: item.membershipLevel,
				price: item.plan.price,
			};
		}

		// eslint-disable-next-line no-console
		console.log('Unimplemented product type.');
		return {
			product_type: type,
		};
	}

	cacheUrl(url: string) {
		this.url = url;
	}

	isUrlCached(url: string) {
		return this.url === url;
	}

	getSessionId() {
		return this.posthog.get_session_id();
	}

	/**
	 * Returns the generated distinct id for anonymous/guest users.
	 */
	getDistinctId() {
		return this.posthog.get_distinct_id();
	}

	private initPostHog() {
		this.ngZone.runOutsideAngular(() => {
			this.posthog = posthog.init(environment.posthogKey, {
				api_host: 'https://t.writtenwordmedia.com',
				ui_host: 'https://us.posthog.com',
				defaults: '2026-01-30',
				capture_pageview: 'history_change',
			});
		});
	}
}
